import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardPageHeader from "../../components/Dashboard/DashboardPageHeader";
import { getShowingText } from "../../i18n/dashboardHelpers";
import { getContacts } from "../../services/apiService";

const PAGE_SIZE = 20;

function extractItems(response) {
  if (Array.isArray(response?.data)) return response.data;
  return response?.data?.data ?? [];
}

function extractTotal(response, fallback = 0) {
  return (
    response?.pagination?.totalItems ??
    response?.data?.total ??
    response?.data?.meta?.total ??
    fallback
  );
}

function extractTotalPages(response, totalItems = 0) {
  if (response?.pagination?.totalPages != null) {
    return response.pagination.totalPages;
  }
  return totalItems === 0 ? 0 : Math.ceil(totalItems / PAGE_SIZE);
}

function getPaginationPages(currentPage, totalPages) {
  if (totalPages <= 0) return [];

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function formatDate(value, locale) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function avatarColor(name = "") {
  const colors = ["#DBEAFE", "#D1FAE5", "#FEF3C7", "#FCE7F3", "#E5E7EB"];
  const index = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
}

function getContactName(contact) {
  return contact.full_name || contact.fullName || "—";
}

function Contacts() {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [contacts, setContacts] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadContacts = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getContacts({
          page,
          pageSize: PAGE_SIZE,
        });

        if (cancelled) return;

        const items = extractItems(response);
        const total = extractTotal(response, items.length);
        setContacts(items);
        setTotalItems(total);
        setTotalPages(extractTotalPages(response, total));
      } catch (fetchError) {
        if (cancelled) return;

        setContacts([]);
        setTotalItems(0);
        setTotalPages(0);
        setError(fetchError?.response?.data?.message || t("dashboard.contacts.loadFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadContacts();

    return () => {
      cancelled = true;
    };
  }, [page, t]);

  const filteredContacts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return contacts;

    return contacts.filter((contact) => {
      const name = getContactName(contact).toLowerCase();
      const email = contact.email?.toLowerCase() ?? "";
      const industry = contact.industry?.toLowerCase() ?? "";
      const country = contact.country?.toLowerCase() ?? "";
      const description = contact.description?.toLowerCase() ?? "";

      return (
        name.includes(query) ||
        email.includes(query) ||
        industry.includes(query) ||
        country.includes(query) ||
        description.includes(query)
      );
    });
  }, [contacts, search]);

  const paginationPages = getPaginationPages(page, totalPages);
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItems);

  return (
    <div className="dashboard-page">
      <DashboardPageHeader title={t("dashboard.contacts.title")}>
        <label className="dashboard-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            placeholder={t("dashboard.contacts.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label={t("dashboard.contacts.searchAria")}
          />
        </label>
      </DashboardPageHeader>

      <div className="dashboard-card dashboard-card--table">
        <div className="dashboard-table-toolbar">
          <h2 className="dashboard-card__title dashboard-card__title--inline">{t("dashboard.contacts.allContacts")}</h2>
        </div>

        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>{t("dashboard.common.customer")}</th>
                <th>{t("dashboard.common.email")}</th>
                <th>{t("dashboard.common.industry")}</th>
                <th>{t("dashboard.common.country")}</th>
                <th>{t("dashboard.common.description")}</th>
                <th>{t("dashboard.common.submitted")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>{t("dashboard.contacts.loading")}</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6}>{error}</td>
                </tr>
              ) : filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan={6}>{t("dashboard.contacts.noContacts")}</td>
                </tr>
              ) : (
                filteredContacts.map((contact) => {
                  const name = getContactName(contact);
                  return (
                    <tr key={contact.id}>
                      <td>
                        <div className="dashboard-table__customer">
                          <span
                            className="dashboard-table__avatar"
                            style={{ backgroundColor: avatarColor(name) }}
                            aria-hidden="true"
                          >
                            {getInitials(name)}
                          </span>
                          <span className="dashboard-table__name">{name}</span>
                        </div>
                      </td>
                      <td className="dashboard-table__email">{contact.email || "—"}</td>
                      <td>{contact.industry || "—"}</td>
                      <td>{contact.country || "—"}</td>
                      <td>{contact.description || "—"}</td>
                      <td className="dashboard-table__date">
                        {formatDate(contact.created_at || contact.createdAt, i18n.language)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-table-footer">
          <p className="dashboard-table-footer__info">
            {getShowingText(t, {
              entity: "contacts",
              start: rangeStart,
              end: rangeEnd,
              total: totalItems,
            })}
          </p>
          <nav className="dashboard-pagination" aria-label={t("dashboard.contacts.paginationAria")}>
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.previousPage")}
              disabled={page <= 1 || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {paginationPages.map((pageNumber, index) =>
              pageNumber === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="dashboard-pagination__ellipsis" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={pageNumber}
                  type="button"
                  className={`dashboard-pagination__page${
                    page === pageNumber ? " dashboard-pagination__page--active" : ""
                  }`}
                  aria-current={page === pageNumber ? "page" : undefined}
                  disabled={loading}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.nextPage")}
              disabled={page >= totalPages || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

export default Contacts;
